import { useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { confirmDialog } from 'primereact/confirmdialog';
import Cookies from 'universal-cookie';
import { Auth } from 'aws-amplify';
import { logout } from 'utils/utils';

const cookies = new Cookies();

export default function useAutoLogoutOnIdle(options = {}) {
  const { minutes = Number(process.env.REACT_APP_IDLE_LOGOUT_MINUTES || 120), websocketContext = null, history = null } = options;

  const hasLoggedOutRef = useRef(false);
  const storageKey = 'lpms:auto-logout';
  const lastActivityKey = 'lpms:last-activity';
  const warningTimeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const performLogout = async (skipStorageUpdate = false) => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    if (!skipStorageUpdate) {
      localStorage.setItem(storageKey, Date.now().toString());
    }
    localStorage.removeItem(lastActivityKey);
    try {
      if (websocketContext && typeof websocketContext.closeConnection === 'function') {
        websocketContext.closeConnection();
      }
      logout(history);
    } catch (e) {
      console.log(e);
    }
  };

  // Update shared activity timestamp
  const updateActivity = () => {
    localStorage.setItem(lastActivityKey, Date.now().toString());
  };

  // Check if user has been idle across all tabs
  const checkGlobalIdle = () => {
    const lastActivity = localStorage.getItem(lastActivityKey);
    if (!lastActivity) return false;

    const timeoutMs = Math.max(1, minutes) * 60 * 1000;
    const now = Date.now();
    const timeSinceActivity = now - parseInt(lastActivity, 10);

    return timeSinceActivity >= timeoutMs;
  };

  // Cross-tab listener: when any tab triggers auto-logout, all tabs follow
  useEffect(() => {
    const onStorage = (e) => {
      if (e && e.key === storageKey && e.newValue) {
        // Clear the periodic check interval since we're about to logout
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }

        // Show logout dialog in all tabs (triggered by storage event)
        onIdle(true);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []); // eslint-disable-line

  // Initialize activity tracking and periodic checks
  useEffect(() => {
    const token = cookies.get('token');
    if (!token) return;

    // Initialize last activity timestamp
    updateActivity();

    // Periodic check for global idle state
    const checkInterval = setInterval(() => {
      const token = cookies.get('token');
      if (!token) {
        clearInterval(checkInterval);
        return;
      }

      if (checkGlobalIdle()) {
        clearInterval(checkInterval);
        // First set the storage key to trigger other tabs
        localStorage.setItem(storageKey, Date.now().toString());
        // Then show modal in this tab
        onIdle(false);
      }
    }, 30000); // Check every 30 seconds

    checkIntervalRef.current = checkInterval;

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, []); // eslint-disable-line

  // Activity event listeners
  useEffect(() => {
    const token = cookies.get('token');
    if (!token) return;

    const activityEvents = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'wheel', 'pointerdown', 'click'];

    const handleActivity = () => {
      updateActivity();
    };

    // Throttle activity updates to avoid excessive localStorage writes
    let throttleTimeout;
    const throttledHandleActivity = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        handleActivity();
        throttleTimeout = null;
      }, 500);
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledHandleActivity, true);
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledHandleActivity, true);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, []); // eslint-disable-line

  const onIdle = () => {
    // Only act when user is authenticated
    const token = cookies.get('token');
    if (!token) return;

    // Clear the periodic check interval since we're about to logout
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    Auth.signOut().catch((e) => {
      console.log(e);
    });

    // Show confirmation dialog before logging out
    confirmDialog({
      message: 'Due to inactivity, you have been automatically logged out. \nPlease sign in again to continue.',
      header: 'Session Timeout',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Okay',
      rejectLabel: '',
      acceptClassName: 'p-button-primary',
      rejectClassName: 'p-button-secondary d-none',
      closable: false,
      footerClassName: 'd-flex justify-content-center',
      style: { width: 400 },
      accept: () => {
        // User chose to logout
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
          warningTimeoutRef.current = null;
        }
        // Always skip storage update since it was already set in the periodic check
        // or by another tab
        performLogout(true);
      },
      reject: () => null,
    });
  };
}
